<template>
  <DpplDarkTooltip
    v-if="!showDropdown"
    :tooltip-offset="8"
    :hide-tooltip="false"
  >
    <template #activator="{ onMouseenter, onMouseleave }">
      <JobStatusPill
        :status-icon="statusIcon"
        :status-text="statusText"
        :status-class="statusClass"
        class="cursor-pointer"
        @mouseenter="onMouseenter"
        @mouseleave="onMouseleave"
      />
    </template>
    <template #tooltip-content>
      <h5 class="tooltip-title">
        {{ statusHover.title }}
      </h5>
      <p class="tooltip-description">
        {{ statusHover.description }}
      </p>
    </template>
  </DpplDarkTooltip>
  <v-menu v-else open-on-click offset="4px" :disabled="isRevisionSaving">
    <template #activator="{ props: activatorProps, isActive }">
      <JobStatusPill
        v-bind="activatorProps"
        :status-icon="statusIcon"
        :status-text="statusText"
        :status-class="statusClass"
        show-hover-effect
        class="cursor-pointer"
        :loading="loading"
        :disabled="isRevisionSaving"
      >
        <template #append>
          <v-icon :icon="isActive ? '$chevron-up' : '$chevron-down'" size="15px" />
        </template>
      </JobStatusPill>
    </template>
    <div class="menu-content">
      <div
        v-for="({
          label, isCurrent, icon, class: itemClass, description, action,
        }) in options"
        :key="label"
        class="menu-item"
        :class="{ current: isCurrent }"
        @click="!isCurrent && handleActions(action)"
      >
        <div class="d-flex align-center">
          <JobStatusPill
            :status-icon="icon"
            :status-text="label"
            :status-class="itemClass"
          />
          <span
            v-if="isCurrent"
            class="ml-2 current-text"
          >
            Current
          </span>
        </div>
        <p class="description mt-2">
          {{ description }}
        </p>
      </div>
    </div>
  </v-menu>
</template>

<script setup>
import { computed, ref } from 'vue';
import JobStatusPill from '@/components/editor/JobStatusPill.vue';
import { useEditor } from '@/stores/editor/editor';
import { storeToRefs } from 'pinia';
import { useAppAuthData } from '@/stores/app/authData';
import { useRoute, useRouter } from 'vue-router';
import DpplDarkTooltip from '@/components/datapeople/DpplDarkTooltip.vue';
import { useTracking } from '@/stores/tracking/tracking';

const props = defineProps({
  readOnly: { type: Boolean, default: false },
});

const {
  jobId,
  jobStatusLabel,
  isPermissionTogglable,
  isPrivate,
  isFlagship,
  isHidden,
  isTemplate,
  isEvergreen,
  isSharePage,
  isExternal,
  isRevisionSaving,
} = storeToRefs(useEditor());

const { isAdmin } = storeToRefs(useAppAuthData());

const {
  unmakeFlagship,
  duplicateAndMakeFlagship,
  updateIsHidden,
  makeDraftPublic,
  makeDraftPrivate,
} = useEditor();

const router = useRouter();
const route = useRoute();

const reloadJob = (id) => router
  .replace({
    name: 'editor-view-reload',
    params: { id },
    query: { ...route.query },
  })
  .catch(() => {});

const loading = ref(false);

const dropdownOptionsMap = computed(() => ({
  flagship: ['flagship', 'flagship-hidden', 'draft'],
  'flagship-hidden': ['flagship-hidden', 'flagship', 'draft'],
  draft: isAdmin.value ? ['draft', 'draft-private', 'flagship'] : ['draft', 'draft-private'],
  'draft-private': isAdmin.value ? ['draft-private', 'draft', 'flagship'] : ['draft-private', 'draft'],
  open: ['open', 'flagship'],
  unpublished: ['unpublished', 'flagship'],
  template: ['template', 'template-hidden'],
  'template-hidden': ['template-hidden', 'template'],
}));

const statusClass = computed(() => {
  if (isFlagship.value) {
    if (isHidden.value) {
      return 'flagship-hidden';
    }
    return 'flagship';
  }

  if (isTemplate.value) {
    if (isHidden.value) {
      return 'template-hidden';
    }
    return 'template';
  }

  if (jobStatusLabel.value === 'Draft' || jobStatusLabel.value === 'Draft Internal') {
    if (isPrivate.value) {
      return 'draft-private';
    }
    return 'draft';
  }

  if (jobStatusLabel.value === 'Open' || jobStatusLabel.value === 'Open Internal') {
    return 'open';
  }

  if (jobStatusLabel.value === 'Unpublished' || jobStatusLabel.value === 'Unpublished Internal') {
    return 'unpublished';
  }

  return '';
});

const showDropdown = computed(() => {
  if (props.readOnly) {
    return false;
  }

  if (route.query?.view === 'chrome') return false;

  if (isAdmin.value) return true;

  if (['draft', 'draft-private'].includes(statusClass.value)
    && isPermissionTogglable.value
    && !isSharePage.value) return true;

  return false;
});

const options = computed(() => {
  let flagshipDescription = '';
  let flagshipHiddenDescription = '';
  let draftDescription = '';
  let draftPrivateDescription = '';
  let templateDescription = '';
  let templateHiddenDescription = '';
  let publishedDescription = 'This job post is available to anyone in your organization in the Published Library.';
  let unpublishedDescription = 'This job post is available to anyone in your organization in the Unpublished Library.';

  if (isFlagship.value) {
    draftDescription = 'This setting removes the Flagship status and moves this job to the Draft Library.';
  } else if (statusClass.value === 'draft') {
    draftDescription = 'This job post is available to anyone in your organization in the Draft Library.';
    draftPrivateDescription = 'This setting makes this job private to you and explicitly-defined collaborators.';
  } else {
    draftDescription = 'This setting makes this job post available to anyone in your organization.';
    draftPrivateDescription = 'This job post is private to you and explicitly-defined collaborators.';
  }

  if (statusClass.value === 'flagship') {
    flagshipHiddenDescription = 'This setting allows you to edit this Flagship job. This job is only visible and available for editing by JobNav admins.';
    flagshipDescription = 'This job post is available as a Flagship job to anyone in your organization.';
  } else if (statusClass.value === 'flagship-hidden') {
    flagshipHiddenDescription = 'This job post is able to be edited by JobNav admins. It is also hidden from other members of your organization.';
    flagshipDescription = 'This setting makes this Flagship job available to anyone in your organization.';
  } else {
    flagshipDescription = 'This setting creates a copy of this job and identifies it as a best-in-class example for your organization.';
  }

  if (statusClass.value === 'template') {
    templateDescription = 'This template is available for use by anyone in your organization.';
    templateHiddenDescription = 'This setting enables editing. This template is only visible and available for editing by other JobNav admins.';
  } else if (statusClass.value === 'template-hidden') {
    templateHiddenDescription = 'This template can only be edited by JobNav admins. It is hidden from other members of your organization.';
    templateDescription = 'This setting makes this template available for use by anyone in your organization.';
  }

  if (isEvergreen.value && !isFlagship.value) {
    publishedDescription += ' It is designated as an Evergreen by your ATS.';
    draftDescription += ' It is designated as an Evergreen by your ATS.';
    unpublishedDescription += ' It is designated as an Evergreen by your ATS.';
  }

  let publishedLabel = 'Published';
  let unpublishedLabel = 'Unpublished';
  let draftLabel = 'Draft';
  let privateDraftLabel = 'Private Draft';

  if (jobStatusLabel.value === 'Open Internal' || (jobStatusLabel.value === 'Open' && !isExternal.value)) {
    publishedLabel = 'Published Internal';
  } else if (jobStatusLabel.value === 'Unpublished Internal' || (jobStatusLabel.value === 'Unpublished' && !isExternal.value)) {
    unpublishedLabel = 'Unpublished Internal';
  } else if (jobStatusLabel.value === 'Draft Internal' || (jobStatusLabel.value === 'Draft' && !isExternal.value)) {
    draftLabel = 'Draft Internal';
    privateDraftLabel = 'Private Draft Internal';
  }

  if (isEvergreen.value) {
    publishedLabel += ' Evergreen';
    unpublishedLabel += ' Evergreen';
    draftLabel += ' Evergreen';
    privateDraftLabel += ' Evergreen';
  }

  const allOptions = [
    {
      label: 'Flagship',
      icon: '$flag-variant',
      class: 'flagship',
      isCurrent: statusClass.value === 'flagship',
      description: flagshipDescription,
      action: async () => {
        if (!isFlagship.value) {
          useTracking().flagshipFlaggedAsFlagship();

          const { id } = await duplicateAndMakeFlagship({ jobId: jobId.value });
          if (id) {
            await reloadJob(id);
          }
        } else {
          useTracking().flagshipVisibility({ isHidden: false, origin: 'Single Job Page' });
          await updateIsHidden(false, 'flagship');
          // Reload the editor to get updated isEditable status
          await reloadJob(jobId.value);
        }
      },
    },
    {
      label: 'Hidden Flagship',
      icon: '$eye-off-outline',
      class: 'flagship-hidden',
      isCurrent: statusClass.value === 'flagship-hidden',
      description: flagshipHiddenDescription,
      action: async () => {
        useTracking().flagshipVisibility({ isHidden: true, origin: 'Single Job Page' });
        await updateIsHidden(true, 'flagship');
        // Reload the editor to get updated isEditable status
        await reloadJob(jobId.value);
      },
    },
    {
      label: draftLabel,
      icon: isEvergreen.value ? '$sync' : '',
      class: 'draft',
      isCurrent: statusClass.value === 'draft',
      description: draftDescription,
      action: async () => {
        if (isFlagship.value) {
          useTracking().flagshipRemoveFlagship();
          await unmakeFlagship({ jobId: jobId.value });
          // Reload the editor to get updated isEditable status
          await reloadJob(jobId.value);
        } else {
          useTracking().draftPostStatusToggle({ status: 'Public' });
          await makeDraftPublic({ jobId: jobId.value });
        }
      },
    },
    {
      label: privateDraftLabel,
      icon: '$eye-off-outline',
      class: 'draft-private',
      isCurrent: statusClass.value === 'draft-private',
      description: draftPrivateDescription,
      action: async () => {
        useTracking().draftPostStatusToggle({ status: 'Private' });
        await makeDraftPrivate({ jobId: jobId.value });
      },
    },
    {
      label: publishedLabel,
      icon: isEvergreen.value ? '$sync' : '$checkbox-marked-circle',
      class: 'open',
      isCurrent: statusClass.value === 'open',
      description: publishedDescription,
    },
    {
      label: unpublishedLabel,
      icon: isEvergreen.value ? '$sync' : '$eye-off-outline',
      class: 'unpublished',
      isCurrent: statusClass.value === 'unpublished',
      description: unpublishedDescription,
    },
    {
      label: 'Template',
      icon: '',
      class: 'template',
      isCurrent: statusClass.value === 'template',
      description: templateDescription,
      action: async () => {
        useTracking().templateHideShowTemplate({ isHidden: false, origin: 'Single Job Page' });
        await updateIsHidden(false);
      },
    },
    {
      label: 'Hidden Template',
      icon: '$eye-off-outline',
      class: 'template-hidden',
      isCurrent: statusClass.value === 'template-hidden',
      description: templateHiddenDescription,
      action: async () => {
        useTracking().templateHideShowTemplate({ isHidden: true, origin: 'Single Job Page' });
        await updateIsHidden(true);
      },
    },
  ];

  const allOptionsMap = allOptions.reduce((acc, opt) => {
    acc[opt.class] = opt;
    return acc;
  }, {});

  let opts = (dropdownOptionsMap.value[statusClass.value] || [])
    .map((option) => allOptionsMap[option])
    .filter(Boolean);

  // Hide draft/private-draft toggle if not permission togglable or a share page
  if (!isPermissionTogglable.value || isSharePage.value) {
    if (statusClass.value === 'draft') {
      opts = opts.filter((opt) => opt.class !== 'draft-private');
    } else if (statusClass.value === 'draft-private') {
      opts = opts.filter((opt) => opt.class !== 'draft');
    }
  }

  return opts;
});

const statusText = computed(() => options.value
  .find((option) => option.isCurrent)?.label || jobStatusLabel.value);
const statusIcon = computed(() => options.value
  .find((option) => option.isCurrent)?.icon || '');

const statusHover = computed(() => ({
  flagship: {
    title: 'A best-in-class job post handpicked by your admins',
    description: 'Flagship jobs are read-only. To create a new draft from this job, use the Duplicate button.',
  },
  template: {
    title: 'Organization-wide Template',
    description: 'This template contains standard company info to ensure consistency across job posts.',
  },
  'flagship-hidden': {
    title: 'Temporarily hidden Flagship',
    description: 'This Flagship Job is currently hidden while it is being edited. It isn\'t available for use by your team.',
  },
  'template-hidden': {
    title: 'Temporarily hidden Template',
    description: 'This template is currently hidden while it is being edited. It isn\'t available for use by your team.',
  },
  draft: {
    title: 'JobNav Draft',
    description: 'This job post is not yet published, not connected with any ATS jobs, and is visible to all JobNav users.',
  },
  'draft-private': {
    title: 'Private JobNav Draft',
    description: 'This job post is not yet published, not connected with any ATS jobs, and is only visible to users you\'ve shared this job with in JobNav.',
  },
  open: {
    title: 'Live and open to applicants',
    description: isEvergreen.value
      ? 'This Evergreen job post is currently published to at least one job board and is currently accepting applications.'
      : 'This job post is currently published to at least one job board and is currently accepting applications.',
  },
  unpublished: {
    title: 'No longer open to applicants',
    description: isEvergreen.value
      ? 'This Evergreen job post was previously published but has since been removed from all job boards and is no longer accepting applications.'
      : 'This job post was previously published but has been removed from all job boards and is no longer accepting applications.',
  },
}[statusClass.value] || { title: '', description: '' }));

const handleActions = async (action) => {
  if (!action) return;

  loading.value = true;

  await action();

  loading.value = false;
};
</script>

<style lang="less" scoped>
.menu-content {
  width: 310px;
  background: @white;
  border: 1px solid @stroke-default;
  border-radius: 7px;
  display: flex;
  flex-direction: column;
}

.menu-item {
  padding: 12px;

  &:not(:last-child) {
    border-bottom: 1px solid @stroke-default;
  }

  &:last-child {
    border-radius: 0 0 7px 7px;
  }

  &:not(.current):hover {
    background: @bg-canvas;
    cursor: pointer;
  }

}

.description {
  font-size: 13px;
  line-height: 150%;
  color: @text-light;
  font-weight: 500;
}

.current-text {
  font-size: 12px;
  line-height: 150%;
  font-weight: 500;
  color: @text-light;
}

.tooltip-title {
  font-size: 14px;
  font-weight: 500;
  line-height: 120%;
  color: @white;
  margin-bottom: 2px;
}
.tooltip-description {
  font-size: 13px;
  line-height: 120%;
  color: @grey-500;
  font-weight: 500;
}
</style>
